import { GoogleGenAI } from "@google/genai";
import CONFIG from "../config/config";

export interface GeminiImageGenerationRequest {
  prompt: string;
  imageData?: string | string[];
  model?: string;
  aspectRatio?: string;
}

export interface GeminiImageGenerationResponse {
  success: boolean;
  imageUrl?: string;
  error?: string;
}

class GeminiService {
  private ai: GoogleGenAI;
  private apiKey: string;

  constructor() {
    this.apiKey = CONFIG.GEMINI_API_KEY;
    if (!this.apiKey) {
      console.warn('Cannot connect to the AI model.');
    }
    this.ai = new GoogleGenAI({ apiKey: this.apiKey });
  }

  /**
   * Generate an image using Gemini AI
   */
  async generateImage(request: GeminiImageGenerationRequest): Promise<GeminiImageGenerationResponse> {
    try {
      if (!this.apiKey) {
        return {
          success: false,
          error: 'Cannot connect to the AI model.'
        };
      }

      const model = request.model || "gemini-2.5-flash-image";
      
      const parts: any[] = [
        {
          text: request.prompt
        }
      ];

      if (request.imageData) {
        const imageArray = Array.isArray(request.imageData) ? request.imageData : [request.imageData];
        imageArray.forEach((imageData) => {
          parts.push({
            inlineData: {
              mimeType: "image/jpeg",
              data: imageData
            }
          });
        });
      }

      const contents = [
        {
          parts
        }
      ];

      const aspectRatio = request.aspectRatio || "9:16";
      
      const response = await this.ai.models.generateContent({
        model,
        contents,
        config: {
          responseModalities: ['Image'],
          imageConfig: {
            aspectRatio: aspectRatio,
          },        }
      });

      if (response.candidates && response.candidates.length > 0 && response.candidates[0].content?.parts) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData) {
            const imageData = part.inlineData.data;
            const imageUrl = `data:image/png;base64,${imageData}`;
            
            return {
              success: true,
              imageUrl
            };
          }
        }
      }

      return {
        success: false,
        error: 'No image data found in the response'
      };

    } catch (error) {
      console.error('Error generating image with Gemini:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Check if the service is properly configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get the current API key status (for debugging)
   */
  getApiKeyStatus(): string {
    return this.apiKey ? 'Configured' : 'Not configured';
  }
}

// Export a singleton instance
export const geminiService = new GeminiService();
export default geminiService;
